import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  List as ListIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  History as HistoryIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
} from "@mui/icons-material";

import { plentyArticlesApi } from "../../../../services/api/plenty/articles";
import { stockApi } from "../../../../services/api/plenty/stock";
import { plentyCredentialsApi } from "../../../../services/api/plenty/plenty_credentials";

const ArticlesManagementSubTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Purchase History Dialog states
  const [purchaseHistoryDialog, setPurchaseHistoryDialog] = useState(false);
  const [purchaseHistoryData, setPurchaseHistoryData] = useState(null);
  const [purchaseHistoryLoading, setPurchaseHistoryLoading] = useState(false);
  const [selectedArticleForHistory, setSelectedArticleForHistory] =
    useState(null);
  const [purchaseHistoryEditDialog, setPurchaseHistoryEditDialog] =
    useState(false);
  const [editingPurchaseRecord, setEditingPurchaseRecord] = useState(null);
  const [purchaseRecordForm, setPurchaseRecordForm] = useState({
    purchase_date: new Date().toISOString().split("T")[0],
    buy_price: "",
    units: "",
    notes: "",
  });

  // Articles Management state
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    sku: "",
    articlename: "",
    articleid: "",
    type: "all",
    source: "all",
    ratio: "",
    ratio_operator: "eq",
    bi_selled_articles_value: "",
    bi_selled_articles_operator: "eq",
    bi_netto_stock_value: "",
    bi_netto_stock_operator: "eq",
    gross_revenue_value: "",
    gross_revenue_operator: "eq",
    stock_value_value: "",
    stock_value_operator: "eq",
    order_by: null,
    order_direction: "asc",
    skip: 0,
    limit: 50,
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [searchResults, setSearchResults] = useState({
    items: [],
    total: 0,
    skip: 0,
    limit: 50,
    has_more: false,
  });
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pageInputValue, setPageInputValue] = useState("");

  // History states
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [historyData, setHistoryData] = useState({});
  const [historyLoading, setHistoryLoading] = useState({});
  const [historyDays, setHistoryDays] = useState({});

  const [totalStockValueArticles, setTotalStockValueArticles] = useState(null); // For Articles tab display

  // Export Dialog states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportParams, setExportParams] = useState({
    format: "xlsx",
    source: "",
    article_type: "",
    sort_by: "stock_value",
    sort_order: "desc",
    latest_only: true,
  });
  const [exportLoading, setExportLoading] = useState(false);

  // SKUs without purchase history states
  const [skusWithoutHistory, setSkusWithoutHistory] = useState(null);
  const [skusWithoutHistoryLoading, setSkusWithoutHistoryLoading] =
    useState(false);
  const [showSkusWithoutHistory, setShowSkusWithoutHistory] = useState(false);

  // Plenty credentials state
  const [plentyCredentials, setPlentyCredentials] = useState([]);
  const [activePlentyCredential, setActivePlentyCredential] = useState(null);

  // Stock Value History states
  const [stockValueHistoryDialog, setStockValueHistoryDialog] = useState(false);
  const [stockValueHistoryData, setStockValueHistoryData] = useState(null);
  const [stockValueHistoryLoading, setStockValueHistoryLoading] =
    useState(false);
  const [stockValueHistoryDays, setStockValueHistoryDays] = useState(30);

  // Load Plenty credentials on mount
  useEffect(() => {
    const loadPlentyCredentials = async () => {
      try {
        const response = await plentyCredentialsApi.getCredentials();
        const credentials = response.data || [];
        setPlentyCredentials(credentials);

        // Find the active credential
        const activeCred = credentials.find(
          (cred) => cred.active && cred.status === "Connected",
        );
        setActivePlentyCredential(activeCred);
      } catch (error) {
        console.error("Failed to load Plenty credentials:", error);
      }
    };

    loadPlentyCredentials();
  }, []);

  const loadTotalStockValue = async (force = false) => {
    try {
      if (!force) {
        const cachedData = localStorage.getItem("totalStockValueData");
        if (cachedData) {
          setTotalStockValueArticles(JSON.parse(cachedData));
          return;
        }
      }
      const response = await stockApi.getTotalStockValue();
      setTotalStockValueArticles(response.data);
      localStorage.setItem(
        "totalStockValueData",
        JSON.stringify(response.data),
      );
    } catch (error) {
      setError("Failed to load total stock value for articles tab.");
      console.error(
        "Failed to load total stock value for articles tab:",
        error,
      );
    }
  };

  const loadStockValueHistory = async (days = stockValueHistoryDays) => {
    setStockValueHistoryLoading(true);
    try {
      const response = await stockApi.getTotalStockValue(days);
      setStockValueHistoryData(response.data);
      setStockValueHistoryDialog(true);
    } catch (error) {
      setError(`Failed to load stock value history for ${days} days.`);
      console.error("Failed to load stock value history:", error);
    } finally {
      setStockValueHistoryLoading(false);
    }
  };

  // Load total stock value when Articles Management tab is opened
  useEffect(() => {
    loadTotalStockValue();
    loadSkusWithoutHistory();
  }, []);

  // Load SKUs without purchase history
  const loadSkusWithoutHistory = async () => {
    setSkusWithoutHistoryLoading(true);
    try {
      const response = await stockApi.getSkusWithoutPurchaseHistory({
        min_stock: 0, // Only show items with stock
      });
      setSkusWithoutHistory(response.data);
    } catch (error) {
      console.error("Failed to load SKUs without purchase history:", error);
    } finally {
      setSkusWithoutHistoryLoading(false);
    }
  };

  // Handle click on SKU without history to search for it
  const handleSkuWithoutHistoryClick = async (sku) => {
    // Set search params with the SKU
    const newParams = {
      ...searchParams,
      sku: sku,
      skip: 0, // Reset to first page
    };
    setSearchParams(newParams);

    // Execute the search
    await handleSearch(newParams);

    // Close the SKUs without history section
    setShowSkusWithoutHistory(false);
  };

  // Purchase History handler
  const handleViewPurchaseHistory = async (article) => {
    setSelectedArticleForHistory(article);
    setPurchaseHistoryDialog(true);
    setPurchaseHistoryLoading(true);
    setPurchaseHistoryData(null);

    try {
      const response = await stockApi.getAllStockDataBySku(article.sku);
      setPurchaseHistoryData(response.data);
    } catch (error) {
      console.error("Failed to load purchase history:", error);
      // Set empty data structure instead of error to allow adding records
      setPurchaseHistoryData({
        article: article,
        purchase_records: [],
        average_cost: 0,
      });
    } finally {
      setPurchaseHistoryLoading(false);
    }
  };

  const handleAddPurchaseRecord = () => {
    setEditingPurchaseRecord(null);
    setPurchaseRecordForm({
      purchase_date: new Date().toISOString().split("T")[0],
      buy_price: "",
      units: "",
      notes: "",
    });
    setPurchaseHistoryEditDialog(true);
  };

  const handleEditPurchaseRecord = (record) => {
    setEditingPurchaseRecord(record);
    setPurchaseRecordForm({
      purchase_date: new Date(record.purchase_date).toISOString().split("T")[0],
      buy_price: record.buy_price.toString(),
      units: record.units.toString(),
      notes: record.notes || "",
    });
    setPurchaseHistoryEditDialog(true);
  };

  const handleSavePurchaseRecord = async () => {
    if (!selectedArticleForHistory) return;

    const buyPrice = parseFloat(purchaseRecordForm.buy_price);
    const units = parseInt(purchaseRecordForm.units);

    if (!purchaseRecordForm.purchase_date || isNaN(buyPrice) || isNaN(units)) {
      setError(
        "Please fill in all fields with valid values (buy price and units must be greater than 0)",
      );
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const recordData = {
        sku: selectedArticleForHistory.sku,
        purchase_date: purchaseRecordForm.purchase_date,
        buy_price: buyPrice,
        units: units,
        notes: purchaseRecordForm.notes || `Added manually by user`,
        href: "Human", // Mark as human-created
      };

      if (editingPurchaseRecord) {
        // Update existing record
        await stockApi.updatePurchaseRecord(
          editingPurchaseRecord.id,
          recordData,
        );
        setSuccess("Purchase record updated successfully!");
      } else {
        // Create new record
        await stockApi.createPurchaseRecord(recordData);
        setSuccess("Purchase record created successfully!");
      }

      // Reload purchase history
      const response = await stockApi.getAllStockDataBySku(
        selectedArticleForHistory.sku,
      );
      setPurchaseHistoryData(response.data);

      setPurchaseHistoryEditDialog(false);
      setEditingPurchaseRecord(null);
      setPurchaseRecordForm({
        purchase_date: new Date().toISOString().split("T")[0],
        buy_price: "",
        units: "",
        notes: "",
      });
    } catch (error) {
      console.error("Failed to save purchase record:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePurchaseRecord = async (recordId) => {
    if (
      !window.confirm("Are you sure you want to delete this purchase record?")
    ) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await stockApi.deletePurchaseRecord(recordId);
      setSuccess("Purchase record deleted successfully!");

      // Reload purchase history to get updated data
      if (selectedArticleForHistory) {
        try {
          const response = await stockApi.getAllStockDataBySku(
            selectedArticleForHistory.sku,
          );
          setPurchaseHistoryData(response.data);
        } catch (reloadError) {
          // If 404, it means all purchase history was deleted - set empty state
          if (reloadError.response?.status === 404) {
            setPurchaseHistoryData({
              sku: selectedArticleForHistory.sku,
              article: {
                bi_netto_stock: selectedArticleForHistory.bi_netto_stock || 0,
                stock_value: null,
                articlename: selectedArticleForHistory.articlename,
              },
              total_records: 0,
              purchase_records: [],
              average_cost: 0,
              statistics: null,
            });
          } else {
            throw reloadError;
          }
        }

        // Refresh the total stock value and articles without history
        await loadTotalStockValue();
        await loadSkusWithoutHistory();
      }
    } catch (error) {
      console.error("Failed to delete purchase record:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseRecordFormChange = (field, value) => {
    setPurchaseRecordForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper function to generate PlentyMarkets item URL
  const getPlentyItemUrl = (articleId, variantId) => {
    if (!activePlentyCredential || !articleId || !variantId) {
      return null;
    }

    return `https://p${activePlentyCredential.plenty_id}.my.plentysystems.com/plenty/terra/item/my-item/${articleId}/${variantId}`;
  };

  // Articles Management functions
  const handleSearch = async (paramsOverride = null) => {
    try {
      setArticlesLoading(true);
      setError(null);

      const params = paramsOverride || searchParams;
      const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
        // Skip empty, null, undefined, and "all" values
        if (
          value !== "" &&
          value !== null &&
          value !== undefined &&
          value !== "all"
        ) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await plentyArticlesApi.searchArticles(cleanParams);
      setSearchResults(response.data);
      setArticles(response.data.items);
    } catch (error) {
      console.error("Error searching articles:", error);
      setError(getErrorMessage(error));
    } finally {
      setArticlesLoading(false);
    }
  };

  const handleLoadAllArticles = async () => {
    try {
      setArticlesLoading(true);
      setError(null);

      const response = await plentyArticlesApi.getArticles({
        skip: searchParams.skip,
        limit: searchParams.limit,
      });
      setSearchResults(response.data);
      setArticles(response.data.items);
    } catch (error) {
      console.error("Error loading articles:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to load articles",
      );
    } finally {
      setArticlesLoading(false);
    }
  };

  const handleEditArticle = async (updatedArticle) => {
    try {
      setLoading(true);
      setError(null);

      await plentyArticlesApi.updateArticle(
        updatedArticle.variantid,
        updatedArticle,
      );

      // Refresh the current view
      if (searchResults.items.length > 0) {
        await handleSearch();
      }

      setSuccess("Article updated successfully!");
      setEditDialogOpen(false);
      setSelectedArticle(null);
    } catch (error) {
      console.error("Error updating article:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to update article",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFetchHistory = async (sku, days = 30) => {
    try {
      setHistoryLoading((prev) => ({ ...prev, [sku]: true }));

      const response = await plentyArticlesApi.getArticleHistory(sku, days);

      setHistoryData((prev) => ({
        ...prev,
        [sku]: response.data.snapshots,
      }));

      setHistoryDays((prev) => ({ ...prev, [sku]: days }));
    } catch (error) {
      console.error("Error fetching article history:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to fetch article history",
      );
    } finally {
      setHistoryLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleToggleHistory = async (article) => {
    if (expandedArticle === article.sku) {
      // Collapse
      setExpandedArticle(null);
    } else {
      // Expand
      setExpandedArticle(article.sku);

      // Fetch history if not already loaded
      if (!historyData[article.sku]) {
        await handleFetchHistory(article.sku, 30);
      }
    }
  };

  const handleRefreshHistory = async (sku) => {
    const days = historyDays[sku] || 30;
    await handleFetchHistory(sku, days);
  };

  const handleChangeDays = async (sku, days) => {
    await handleFetchHistory(sku, days);
  };

  const handleDeleteArticle = async () => {
    if (!selectedArticle) return;

    try {
      setLoading(true);
      setError(null);

      await plentyArticlesApi.deleteArticle(selectedArticle.variantid);

      // Refresh the current view
      if (searchResults.items.length > 0) {
        await handleSearch();
      }

      setSuccess("Article deleted successfully!");
      setDeleteDialogOpen(false);
      setSelectedArticle(null);
    } catch (error) {
      console.error("Error deleting article:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to delete article",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearchParamChange = (field, value) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    // Update searchParams with sort info and trigger new search
    const updatedParams = {
      ...searchParams,
      order_by: key,
      order_direction: direction,
      skip: 0, // Reset to first page when sorting changes
    };

    setSearchParams(updatedParams);

    // Trigger search with new sort params
    handleSearch(updatedParams);
  };

  const getSortedArticles = () => {
    // No longer needed - backend handles sorting
    return articles;
  };

  const resetSearchParams = () => {
    setSearchParams({
      sku: "",
      articlename: "",
      articleid: "",
      type: "",
      source: "all",
      ratio: "",
      ratio_operator: "eq",
      bi_selled_articles_value: "",
      bi_selled_articles_operator: "eq",
      bi_netto_stock_value: "",
      bi_netto_stock_operator: "eq",
      gross_revenue_value: "",
      gross_revenue_operator: "eq",
      stock_value_value: "",
      stock_value_operator: "eq",
      order_by: null,
      order_direction: "asc",
      skip: 0,
      limit: 50,
    });
    setSortConfig({ key: null, direction: "asc" });
    setPageInputValue("");
  };

  const handleGoToPage = (pageNumber) => {
    const totalPages = Math.ceil(searchResults.total / searchParams.limit);
    if (pageNumber < 1 || pageNumber > totalPages) {
      setError(`Please enter a page number between 1 and ${totalPages}`);
      return;
    }
    const newSkip = (pageNumber - 1) * searchParams.limit;
    const updatedParams = { ...searchParams, skip: newSkip };
    setSearchParams(updatedParams);
    handleSearch(updatedParams);
    setPageInputValue("");
  };

  const handleGoToFirstPage = () => {
    const updatedParams = { ...searchParams, skip: 0 };
    setSearchParams(updatedParams);
    handleSearch(updatedParams);
  };

  const handleGoToLastPage = () => {
    const totalPages = Math.ceil(searchResults.total / searchParams.limit);
    const lastPageSkip = (totalPages - 1) * searchParams.limit;
    const updatedParams = { ...searchParams, skip: lastPageSkip };
    setSearchParams(updatedParams);
    handleSearch(updatedParams);
  };

  return (
    <>
      <Grid container spacing={3}>
        {/* Total Stock Value Card */}
        {totalStockValueArticles && (
          <Grid item xs={12}>
            <Card sx={{ backgroundColor: "#2e7d32", color: "white" }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="h6" sx={{ mb: 0 }}>
                        Total Stock Value
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.5em",
                          opacity: 0.8,
                          verticalAlign: "middle",
                          mr: 1,
                          textTransform: "uppercase",
                        }}
                      >
                        Combined:
                      </Box>
                      €
                      {(
                        totalStockValueArticles.combined_stock_value ??
                        totalStockValueArticles.total_stock_value
                      )?.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 4, mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Stock:</strong> €
                        {totalStockValueArticles.total_stock_value?.toLocaleString()}{" "}
                        ({totalStockValueArticles.total_stock_units} units)
                      </Typography>
                      <Typography variant="body2">
                        <strong>Transit:</strong> €
                        {totalStockValueArticles.total_transit_stock_value?.toLocaleString() ||
                          0}{" "}
                        ({totalStockValueArticles.total_transit_units || 0}{" "}
                        units)
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {totalStockValueArticles.articles_with_value} articles
                      processed
                    </Typography>

                    {/* Breakdown by source */}
                    {totalStockValueArticles.by_source && (
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          gap: 3,
                          flexWrap: "wrap",
                        }}
                      >
                        {/* Plenty */}
                        <Box
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            padding: 1.5,
                            borderRadius: 1,
                            flex: "1 1 auto",
                            minWidth: "150px",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.9 }}
                          >
                            Plenty
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              color: "rgb(25, 118, 210)",
                              fontWeight: "bold",
                            }}
                          >
                            €
                            {(
                              totalStockValueArticles.by_source.plenty
                                ?.combined_stock_value ??
                              totalStockValueArticles.by_source.plenty
                                ?.stock_value
                            )?.toLocaleString() || "0"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.8 }}
                          >
                            Stock: €
                            {totalStockValueArticles.by_source.plenty?.stock_value?.toLocaleString() ||
                              "0"}{" "}
                            (
                            {totalStockValueArticles.by_source.plenty
                              ?.stock_units || 0}
                            u)
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.8 }}
                          >
                            Transit: €
                            {totalStockValueArticles.by_source.plenty?.transit_stock_value?.toLocaleString() ||
                              "0"}{" "}
                            (
                            {totalStockValueArticles.by_source.plenty
                              ?.transit_units || 0}
                            u)
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.8, mt: 0.5, display: "block" }}
                          >
                            {totalStockValueArticles.by_source.plenty
                              ?.article_count || 0}{" "}
                            articles
                          </Typography>
                        </Box>

                        {/* Amazon */}
                        <Box
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            padding: 1.5,
                            borderRadius: 1,
                            flex: "1 1 auto",
                            minWidth: "150px",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.9 }}
                          >
                            Amazon
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              color: "rgb(245, 0, 87)",
                              fontWeight: "bold",
                            }}
                          >
                            €
                            {(
                              totalStockValueArticles.by_source.amazon
                                ?.combined_stock_value ??
                              totalStockValueArticles.by_source.amazon
                                ?.stock_value
                            )?.toLocaleString() || "0"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.8 }}
                          >
                            Stock: €
                            {totalStockValueArticles.by_source.amazon?.stock_value?.toLocaleString() ||
                              "0"}{" "}
                            (
                            {totalStockValueArticles.by_source.amazon
                              ?.stock_units || 0}
                            u)
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", opacity: 0.8 }}
                          >
                            Transit: €
                            {totalStockValueArticles.by_source.amazon?.transit_stock_value?.toLocaleString() ||
                              "0"}{" "}
                            (
                            {totalStockValueArticles.by_source.amazon
                              ?.transit_units || 0}
                            u)
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.8, mt: 0.5, display: "block" }}
                          >
                            {totalStockValueArticles.by_source.amazon
                              ?.article_count || 0}{" "}
                            articles
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => loadStockValueHistory()}
                      disabled={stockValueHistoryLoading}
                      sx={{
                        color: "white",
                        borderColor: "rgba(255, 255, 255, 0.5)",
                        "&:hover": {
                          borderColor: "white",
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                      }}
                    >
                      {stockValueHistoryLoading ? (
                        <CircularProgress size={16} sx={{ color: "white" }} />
                      ) : (
                        "View History"
                      )}
                    </Button>
                    <IconButton
                      onClick={loadTotalStockValue}
                      sx={{ color: "white" }}
                      title="Refresh Total Stock Value"
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* SKUs Without Purchase History Warning */}
        {skusWithoutHistory &&
          skusWithoutHistory.total_articles_needing_history > 0 && (
            <Grid item xs={12}>
              <Card sx={{ borderLeft: "4px solid #ff9800" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: showSkusWithoutHistory ? 2 : 0,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Alert
                        severity="warning"
                        sx={{ py: 0, mb: 0 }}
                        icon={false}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          ⚠️ {skusWithoutHistory.total_articles_needing_history}{" "}
                          article
                          {skusWithoutHistory.total_articles_needing_history !==
                          1
                            ? "s"
                            : ""}{" "}
                          need purchase history
                        </Typography>
                        <Typography variant="body2">
                          {skusWithoutHistory.articles_with_no_history || 0}{" "}
                          with no history •{" "}
                          {skusWithoutHistory.articles_with_insufficient_history ||
                            0}{" "}
                          with insufficient history •{" "}
                          {skusWithoutHistory.total_stock_units} units affected
                        </Typography>
                      </Alert>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setShowSkusWithoutHistory(!showSkusWithoutHistory)
                        }
                        endIcon={
                          showSkusWithoutHistory ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )
                        }
                      >
                        {showSkusWithoutHistory ? "Hide" : "Show"} Details
                      </Button>
                    </Box>
                    <IconButton
                      onClick={loadSkusWithoutHistory}
                      disabled={skusWithoutHistoryLoading}
                      title="Refresh"
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Box>

                  {showSkusWithoutHistory && (
                    <Box sx={{ mt: 2 }}>
                      {skusWithoutHistoryLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            p: 2,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <TableContainer
                          component={Paper}
                          sx={{ maxHeight: 400 }}
                        >
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  <strong>SKU</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>ASIN</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Article Name</strong>
                                </TableCell>
                                <TableCell align="right">
                                  <strong>Stock</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Status</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Source</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Snapshot Date</strong>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {skusWithoutHistory.articles.map((article) => (
                                <TableRow
                                  key={article.sku}
                                  hover
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover": {
                                      backgroundColor: "#f5f5f5",
                                    },
                                  }}
                                  onClick={() =>
                                    handleSkuWithoutHistoryClick(article.sku)
                                  }
                                >
                                  <TableCell>
                                    <Chip
                                      label={article.sku}
                                      size="small"
                                      color="primary"
                                      clickable
                                    />
                                  </TableCell>
                                  <TableCell>{article.asin || "-"}</TableCell>
                                  <TableCell>
                                    <Tooltip
                                      title={article.articlename || "No name"}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          maxWidth: 300,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {article.articlename || "-"}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={article.bi_netto_stock || 0}
                                      size="small"
                                      color={
                                        article.bi_netto_stock > 0
                                          ? "success"
                                          : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip
                                      title={
                                        article.purchase_history_status ===
                                        "none"
                                          ? "No purchase records found"
                                          : `Has ${article.units_purchased} units of purchase history, missing ${article.units_missing} units`
                                      }
                                    >
                                      <Chip
                                        label={
                                          article.purchase_history_status ===
                                          "none"
                                            ? "No History"
                                            : `Insufficient (${article.units_purchased}/${article.bi_netto_stock})`
                                        }
                                        size="small"
                                        color={
                                          article.purchase_history_status ===
                                          "none"
                                            ? "error"
                                            : "warning"
                                        }
                                      />
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={
                                        article.source?.toUpperCase() || "-"
                                      }
                                      size="small"
                                      color={
                                        article.source === "plenty"
                                          ? "info"
                                          : article.source === "amazon"
                                            ? "error"
                                            : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {article.snapshot_date
                                      ? new Date(
                                          article.snapshot_date,
                                        ).toLocaleDateString()
                                      : "-"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

        {/* Search Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Search Articles
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="SKU"
                    value={searchParams.sku}
                    onChange={(e) =>
                      handleSearchParamChange("sku", e.target.value)
                    }
                    placeholder="Enter SKU to search"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Article Name"
                    value={searchParams.articlename}
                    onChange={(e) =>
                      handleSearchParamChange("articlename", e.target.value)
                    }
                    placeholder="Enter article name"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Article ID"
                    value={searchParams.articleid}
                    onChange={(e) =>
                      handleSearchParamChange("articleid", e.target.value)
                    }
                    placeholder="Enter article ID"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={searchParams.type}
                      label="Type"
                      onChange={(e) =>
                        handleSearchParamChange("type", e.target.value)
                      }
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="neutral">Neutral</MenuItem>
                      <MenuItem value="penner">Penner</MenuItem>
                      <MenuItem value="renner">Renner</MenuItem>
                      <MenuItem value="oos">oos</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Source</InputLabel>
                    <Select
                      value={searchParams.source}
                      label="Source"
                      onChange={(e) =>
                        handleSearchParamChange("source", e.target.value)
                      }
                    >
                      <MenuItem value="all">All Sources</MenuItem>
                      <MenuItem value="plenty">Plenty</MenuItem>
                      <MenuItem value="amazon">Amazon</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleSearch()}
                      disabled={articlesLoading}
                      startIcon={
                        articlesLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SearchIcon />
                        )
                      }
                    >
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={resetSearchParams}
                      disabled={articlesLoading}
                    >
                      Reset
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {/* Advanced Search Options - Business Intelligence Filters */}
              <Box sx={{ mt: 3 }}>
                <Accordion
                  disableGutters
                  sx={{
                    backgroundColor: "background.paper",
                    backgroundImage: "none",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: "text.secondary" }} />
                    }
                    sx={{
                      backgroundColor: "action.hover",
                      "&:hover": { backgroundColor: "action.selected" },
                      "&.Mui-expanded": {
                        backgroundColor: "action.selected",
                        borderBottom: 1,
                        borderColor: "divider",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FilterListIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Business Intelligence Filters
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      pt: 3,
                      backgroundColor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                      }}
                    >
                      {/* Stock Filter */}
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          backgroundColor: "background.default",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          gutterBottom
                        >
                          Stock Quantity
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={searchParams.bi_netto_stock_operator}
                                label="Operator"
                                onChange={(e) =>
                                  handleSearchParamChange(
                                    "bi_netto_stock_operator",
                                    e.target.value,
                                  )
                                }
                              >
                                <MenuItem value="eq">Equal (=)</MenuItem>
                                <MenuItem value="ne">Not Equal (≠)</MenuItem>
                                <MenuItem value="gt">
                                  Greater Than (&gt;)
                                </MenuItem>
                                <MenuItem value="lt">Less Than (&lt;)</MenuItem>
                                <MenuItem value="gte">
                                  Greater Than or Equal (≥)
                                </MenuItem>
                                <MenuItem value="lte">
                                  Less Than or Equal (≤)
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Stock Value"
                              type="number"
                              value={searchParams.bi_netto_stock_value}
                              onChange={(e) =>
                                handleSearchParamChange(
                                  "bi_netto_stock_value",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter quantity"
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Stock Value (€) Filter */}
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          backgroundColor: "background.default",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          gutterBottom
                        >
                          Stock Value (€)
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={searchParams.stock_value_operator}
                                label="Operator"
                                onChange={(e) =>
                                  handleSearchParamChange(
                                    "stock_value_operator",
                                    e.target.value,
                                  )
                                }
                              >
                                <MenuItem value="eq">Equal (=)</MenuItem>
                                <MenuItem value="ne">Not Equal (≠)</MenuItem>
                                <MenuItem value="gt">
                                  Greater Than (&gt;)
                                </MenuItem>
                                <MenuItem value="lt">Less Than (&lt;)</MenuItem>
                                <MenuItem value="gte">
                                  Greater Than or Equal (≥)
                                </MenuItem>
                                <MenuItem value="lte">
                                  Less Than or Equal (≤)
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Stock Value (€)"
                              type="number"
                              value={searchParams.stock_value_value}
                              onChange={(e) =>
                                handleSearchParamChange(
                                  "stock_value_value",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter value in €"
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Sold Articles Filter */}
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          backgroundColor: "background.default",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          gutterBottom
                        >
                          Sold Articles (30 Days)
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={searchParams.bi_selled_articles_operator}
                                label="Operator"
                                onChange={(e) =>
                                  handleSearchParamChange(
                                    "bi_selled_articles_operator",
                                    e.target.value,
                                  )
                                }
                              >
                                <MenuItem value="eq">Equal (=)</MenuItem>
                                <MenuItem value="ne">Not Equal (≠)</MenuItem>
                                <MenuItem value="gt">
                                  Greater Than (&gt;)
                                </MenuItem>
                                <MenuItem value="lt">Less Than (&lt;)</MenuItem>
                                <MenuItem value="gte">
                                  Greater Than or Equal (≥)
                                </MenuItem>
                                <MenuItem value="lte">
                                  Less Than or Equal (≤)
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Sold Articles Value"
                              type="number"
                              value={searchParams.bi_selled_articles_value}
                              onChange={(e) =>
                                handleSearchParamChange(
                                  "bi_selled_articles_value",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter quantity"
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Gross Revenue Filter */}
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          backgroundColor: "background.default",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          gutterBottom
                        >
                          Gross Revenue (30 Days)
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={searchParams.gross_revenue_operator}
                                label="Operator"
                                onChange={(e) =>
                                  handleSearchParamChange(
                                    "gross_revenue_operator",
                                    e.target.value,
                                  )
                                }
                              >
                                <MenuItem value="eq">Equal (=)</MenuItem>
                                <MenuItem value="ne">Not Equal (≠)</MenuItem>
                                <MenuItem value="gt">
                                  Greater Than (&gt;)
                                </MenuItem>
                                <MenuItem value="lt">Less Than (&lt;)</MenuItem>
                                <MenuItem value="gte">
                                  Greater Than or Equal (≥)
                                </MenuItem>
                                <MenuItem value="lte">
                                  Less Than or Equal (≤)
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Gross Revenue Value"
                              type="number"
                              value={searchParams.gross_revenue_value}
                              onChange={(e) =>
                                handleSearchParamChange(
                                  "gross_revenue_value",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter value"
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Ratio Filter */}
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          backgroundColor: "background.default",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          gutterBottom
                        >
                          Stock/Sales Ratio
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={searchParams.ratio_operator || ""}
                                label="Operator"
                                onChange={(e) =>
                                  handleSearchParamChange(
                                    "ratio_operator",
                                    e.target.value,
                                  )
                                }
                              >
                                <MenuItem value="eq">Equal (=)</MenuItem>
                                <MenuItem value="ne">Not Equal (≠)</MenuItem>
                                <MenuItem value="gt">
                                  Greater Than (&gt;)
                                </MenuItem>
                                <MenuItem value="lt">Less Than (&lt;)</MenuItem>
                                <MenuItem value="gte">
                                  Greater Than or Equal (≥)
                                </MenuItem>
                                <MenuItem value="lte">
                                  Less Than or Equal (≤)
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Ratio Value"
                              type="number"
                              value={searchParams.ratio_value || ""}
                              onChange={(e) =>
                                handleSearchParamChange(
                                  "ratio_value",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter ratio value"
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadAllArticles}
                    disabled={articlesLoading}
                    startIcon={<ListIcon />}
                  >
                    Load All Articles
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {searchResults ? `Found ${searchResults.total} articles` : ""}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Results Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Articles
              </Typography>

              {articlesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : articles.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === "sku"}
                            direction={
                              sortConfig.key === "sku"
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort("sku")}
                          >
                            SKU
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === "articlename"}
                            direction={
                              sortConfig.key === "articlename"
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort("articlename")}
                          >
                            Article Name
                          </TableSortLabel>
                        </TableCell>
                        {/* Plenty-specific columns */}
                        {(searchParams.source === "all" ||
                          searchParams.source === "plenty" ||
                          searchParams.source === "both") && (
                          <>
                            <TableCell>
                              <TableSortLabel
                                active={sortConfig.key === "articleid"}
                                direction={
                                  sortConfig.key === "articleid"
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort("articleid")}
                              >
                                Article ID
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={sortConfig.key === "variantid"}
                                direction={
                                  sortConfig.key === "variantid"
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort("variantid")}
                              >
                                Variant ID
                              </TableSortLabel>
                            </TableCell>
                          </>
                        )}

                        {/* Amazon-specific columns */}
                        {(searchParams.source === "all" ||
                          searchParams.source === "amazon" ||
                          searchParams.source === "both") && (
                          <>
                            <TableCell>
                              <TableSortLabel
                                active={sortConfig.key === "asin"}
                                direction={
                                  sortConfig.key === "asin"
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort("asin")}
                              >
                                ASIN
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={sortConfig.key === "roi"}
                                direction={
                                  sortConfig.key === "roi"
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort("roi")}
                              >
                                ROI %
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={sortConfig.key === "days_of_stock_left"}
                                direction={
                                  sortConfig.key === "days_of_stock_left"
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort("days_of_stock_left")}
                              >
                                Days of Stock
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={sortConfig.key === "ratio"}
                                direction={
                                  sortConfig.key === "ratio"
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort("ratio")}
                              >
                                Ratio
                              </TableSortLabel>
                            </TableCell>
                          </>
                        )}

                        {/* Common columns */}
                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === "type"}
                            direction={
                              sortConfig.key === "type"
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort("type")}
                          >
                            Type
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === "source"}
                            direction={
                              sortConfig.key === "source"
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort("source")}
                          >
                            Source
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === "bi_netto_stock"}
                            direction={
                              sortConfig.key === "bi_netto_stock"
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort("bi_netto_stock")}
                          >
                            Stock
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === "stock_value"}
                            direction={
                              sortConfig.key === "stock_value"
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort("stock_value")}
                          >
                            Stock Value
                          </TableSortLabel>
                        </TableCell>

                        {/* Plenty-specific metrics */}
                        {(searchParams.source === "all" ||
                          searchParams.source === "plenty" ||
                          searchParams.source === "both") && (
                          <>
                            <TableCell>
                              <TableSortLabel
                                active={sortConfig.key === "bi_selled_articles"}
                                direction={
                                  sortConfig.key === "bi_selled_articles"
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort("bi_selled_articles")}
                              >
                                Sold Articles
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={sortConfig.key === "gross_revenue"}
                                direction={
                                  sortConfig.key === "gross_revenue"
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort("gross_revenue")}
                              >
                                Gross Revenue
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={sortConfig.key === "ratio"}
                                direction={
                                  sortConfig.key === "ratio"
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort("ratio")}
                              >
                                Ratio
                              </TableSortLabel>
                            </TableCell>
                          </>
                        )}

                        <TableCell>
                          <TableSortLabel
                            active={sortConfig.key === "snapshot_date"}
                            direction={
                              sortConfig.key === "snapshot_date"
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort("snapshot_date")}
                          >
                            Update at
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getSortedArticles().map((article) => (
                        <React.Fragment key={article.sku}>
                          <TableRow>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleHistory(article)}
                                title="View History"
                              >
                                {expandedArticle === article.sku ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              {article.sku ? (
                                getPlentyItemUrl(
                                  article.articleid,
                                  article.variantid,
                                ) ? (
                                  <Button
                                    href={getPlentyItemUrl(
                                      article.articleid,
                                      article.variantid,
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      textTransform: "none",
                                      padding: 0,
                                      minWidth: "auto",
                                      color: "primary.main",
                                      "&:hover": {
                                        textDecoration: "underline",
                                        backgroundColor: "transparent",
                                      },
                                    }}
                                  >
                                    {article.sku}
                                  </Button>
                                ) : (
                                  article.sku
                                )
                              ) : (
                                "N/A"
                              )}
                            </TableCell>
                            <TableCell>
                              {article.articlename || "N/A"}
                            </TableCell>

                            {/* Plenty-specific columns */}
                            {(searchParams.source === "all" ||
                              searchParams.source === "plenty" ||
                              searchParams.source === "both") && (
                              <>
                                <TableCell>
                                  {article.source === "plenty" ||
                                  article.source === "both" ? (
                                    article.articleid || "N/A"
                                  ) : (
                                    <Tooltip title="Not applicable for Amazon items">
                                      <span style={{ color: "#999" }}>—</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {article.source === "plenty" ||
                                  article.source === "both" ? (
                                    article.variantid || "N/A"
                                  ) : (
                                    <Tooltip title="Not applicable for Amazon items">
                                      <span style={{ color: "#999" }}>—</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </>
                            )}

                            {/* Amazon-specific columns */}
                            {(searchParams.source === "all" ||
                              searchParams.source === "amazon" ||
                              searchParams.source === "both") && (
                              <>
                                <TableCell>
                                  {article.source === "amazon" ||
                                  article.source === "both" ? (
                                    article.asin || "N/A"
                                  ) : (
                                    <Tooltip title="Not applicable for Plenty items">
                                      <span style={{ color: "#999" }}>—</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {article.source === "amazon" ||
                                  article.source === "both" ? (
                                    article.roi != null ? (
                                      <Chip
                                        label={`${article.roi.toFixed(1)}%`}
                                        size="small"
                                        color={
                                          article.roi > 20
                                            ? "success"
                                            : article.roi > 10
                                              ? "warning"
                                              : "error"
                                        }
                                      />
                                    ) : (
                                      "N/A"
                                    )
                                  ) : (
                                    <Tooltip title="Not applicable for Plenty items">
                                      <span style={{ color: "#999" }}>—</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {article.source === "amazon" ||
                                  article.source === "both" ? (
                                    article.days_of_stock_left != null ? (
                                      <Chip
                                        label={`${article.days_of_stock_left} days`}
                                        size="small"
                                        color={
                                          article.days_of_stock_left > 30
                                            ? "success"
                                            : article.days_of_stock_left > 14
                                              ? "warning"
                                              : "error"
                                        }
                                      />
                                    ) : (
                                      "N/A"
                                    )
                                  ) : (
                                    <Tooltip title="Not applicable for Plenty items">
                                      <span style={{ color: "#999" }}>—</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {article.source === "amazon" ||
                                  article.source === "both" ? (
                                    article.ratio
                                  ) : (
                                    <Tooltip title="Not applicable for Plenty items">
                                      <span style={{ color: "#999" }}>—</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </>
                            )}

                            {/* Common columns */}
                            <TableCell>{article.type || "N/A"}</TableCell>
                            <TableCell>
                              <Chip
                                label={article.source || "N/A"}
                                size="small"
                                color={
                                  article.source === "plenty"
                                    ? "primary"
                                    : article.source === "amazon"
                                      ? "secondary"
                                      : article.source === "both"
                                        ? "success"
                                        : "default"
                                }
                              />
                            </TableCell>
                            <TableCell>{article.bi_netto_stock}</TableCell>
                            <TableCell>
                              {article.stock_value
                                ? `€${article.stock_value.toFixed(2)}`
                                : "N/A"}
                            </TableCell>

                            {/* Plenty-specific metrics */}
                            {(searchParams.source === "all" ||
                              searchParams.source === "plenty" ||
                              searchParams.source === "both") && (
                              <>
                                <TableCell>
                                  {article.source === "plenty" ||
                                  article.source === "both" ? (
                                    article.bi_selled_articles
                                  ) : (
                                    <Tooltip title="Not applicable for Amazon items">
                                      <span style={{ color: "#999" }}>—</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {article.source === "plenty" ||
                                  article.source === "both" ? (
                                    article.gross_revenue ? (
                                      article.gross_revenue.toFixed(2)
                                    ) : (
                                      "0.00"
                                    )
                                  ) : (
                                    <Tooltip title="Not applicable for Amazon items">
                                      <span style={{ color: "#999" }}>—</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {article.source === "plenty" ||
                                  article.source === "both" ? (
                                    article.ratio
                                  ) : (
                                    <Tooltip title="Not applicable for Amazon items">
                                      <span style={{ color: "#999" }}>—</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </>
                            )}

                            <TableCell>
                              {article.snapshot_date || "N/A"}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() =>
                                    handleViewPurchaseHistory(article)
                                  }
                                  title="View Purchase History"
                                >
                                  <HistoryIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedArticle(article);
                                    setEditDialogOpen(true);
                                  }}
                                  title="Edit Article"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSelectedArticle(article);
                                    setDeleteDialogOpen(true);
                                  }}
                                  title="Delete Article"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* History Row */}
                          {expandedArticle === article.sku && (
                            <TableRow>
                              <TableCell
                                colSpan={18}
                                sx={{ py: 0, bgcolor: "grey.50" }}
                              >
                                <Box sx={{ margin: 2 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                      mb: 2,
                                    }}
                                  >
                                    <Typography variant="subtitle2">
                                      History for {article.sku}
                                    </Typography>
                                    <TextField
                                      size="small"
                                      type="number"
                                      label="Days"
                                      value={historyDays[article.sku] || 30}
                                      onChange={(e) => {
                                        const days = parseInt(
                                          e.target.value,
                                          10,
                                        );
                                        if (days > 0 && days <= 365) {
                                          handleChangeDays(article.sku, days);
                                        }
                                      }}
                                      sx={{ width: 100 }}
                                      inputProps={{ min: 1, max: 365 }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleRefreshHistory(article.sku)
                                      }
                                      title="Refresh History"
                                    >
                                      <RefreshIcon />
                                    </IconButton>
                                  </Box>

                                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Last incoming:{" "}
                                      {article.last_incoming_stock_date ||
                                        "N/A"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Last outgoing:{" "}
                                      {article.last_outgoing_stock_date ||
                                        "N/A"}
                                    </Typography>
                                  </Box>

                                  {historyLoading[article.sku] ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 2,
                                      }}
                                    >
                                      <CircularProgress size={24} />
                                    </Box>
                                  ) : historyData[article.sku] &&
                                    historyData[article.sku].length > 0 ? (
                                    <TableContainer
                                      component={Paper}
                                      sx={{ maxHeight: 300 }}
                                    >
                                      <Table size="small" stickyHeader>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Article Name</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Stock</TableCell>
                                            <TableCell>Sold Articles</TableCell>
                                            <TableCell>Gross Revenue</TableCell>
                                            <TableCell>Ratio</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {historyData[article.sku].map(
                                            (snapshot, idx) => (
                                              <TableRow key={idx}>
                                                <TableCell>
                                                  {snapshot.snapshot_date ||
                                                    "N/A"}
                                                </TableCell>
                                                <TableCell>
                                                  {snapshot.articlename ||
                                                    "N/A"}
                                                </TableCell>
                                                <TableCell>
                                                  {snapshot.type || "N/A"}
                                                </TableCell>
                                                <TableCell>
                                                  {snapshot.bi_netto_stock}
                                                </TableCell>
                                                <TableCell>
                                                  {snapshot.bi_selled_articles}
                                                </TableCell>
                                                <TableCell>
                                                  {snapshot.gross_revenue
                                                    ? snapshot.gross_revenue.toFixed(
                                                        2,
                                                      )
                                                    : "0.00"}
                                                </TableCell>
                                                <TableCell>
                                                  {snapshot.ratio}
                                                </TableCell>
                                              </TableRow>
                                            ),
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  ) : (
                                    <Alert severity="info">
                                      No history data available for this
                                      article.
                                    </Alert>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No articles found. Try adjusting your search criteria or load
                  all articles.
                </Alert>
              )}

              {/* Pagination */}
              {searchResults && searchResults.total > searchParams.limit && (
                <Box sx={{ mt: 3 }}>
                  {/* Main Pagination Controls */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FirstPageIcon />}
                      disabled={searchParams.skip === 0}
                      onClick={handleGoToFirstPage}
                    >
                      First
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={searchParams.skip === 0}
                      onClick={() => {
                        const newSkip = Math.max(
                          0,
                          searchParams.skip - searchParams.limit,
                        );
                        const updatedParams = {
                          ...searchParams,
                          skip: newSkip,
                        };
                        setSearchParams(updatedParams);
                        handleSearch(updatedParams);
                      }}
                    >
                      Previous
                    </Button>
                    <Typography sx={{ mx: 2, alignSelf: "center" }}>
                      Page{" "}
                      {Math.floor(searchParams.skip / searchParams.limit) + 1}{" "}
                      of {Math.ceil(searchResults.total / searchParams.limit)}
                    </Typography>
                    <Button
                      variant="outlined"
                      disabled={
                        searchParams.skip + searchParams.limit >=
                        searchResults.total
                      }
                      onClick={() => {
                        const newSkip = searchParams.skip + searchParams.limit;
                        const updatedParams = {
                          ...searchParams,
                          skip: newSkip,
                        };
                        setSearchParams(updatedParams);
                        handleSearch(updatedParams);
                      }}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<LastPageIcon />}
                      disabled={
                        searchParams.skip + searchParams.limit >=
                        searchResults.total
                      }
                      onClick={handleGoToLastPage}
                    >
                      Last
                    </Button>
                  </Box>

                  {/* Go to Page Input */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Go to page:
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={pageInputValue}
                      onChange={(e) => setPageInputValue(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && pageInputValue) {
                          handleGoToPage(parseInt(pageInputValue));
                        }
                      }}
                      inputProps={{
                        min: 1,
                        max: Math.ceil(
                          searchResults.total / searchParams.limit,
                        ),
                        style: { width: "80px" },
                      }}
                      placeholder="Page #"
                    />
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!pageInputValue}
                      onClick={() => handleGoToPage(parseInt(pageInputValue))}
                    >
                      Go
                    </Button>
                  </Box>

                  {/* Results Summary */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", textAlign: "center", mt: 1 }}
                  >
                    Showing {searchParams.skip + 1} -{" "}
                    {Math.min(
                      searchParams.skip + searchParams.limit,
                      searchResults.total,
                    )}{" "}
                    of {searchResults.total} articles
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Article Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Article</DialogTitle>
        <DialogContent>
          {selectedArticle && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Common fields */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SKU"
                  value={selectedArticle.sku || ""}
                  onChange={(e) =>
                    setSelectedArticle((prev) => ({
                      ...prev,
                      sku: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Article Name"
                  value={selectedArticle.articlename || ""}
                  onChange={(e) =>
                    setSelectedArticle((prev) => ({
                      ...prev,
                      articlename: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={selectedArticle.source || "plenty"}
                    label="Source"
                    disabled
                  >
                    <MenuItem value="plenty">Plenty</MenuItem>
                    <MenuItem value="amazon">Amazon</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={selectedArticle.type || ""}
                    label="Type"
                    onChange={(e) =>
                      setSelectedArticle((prev) => ({
                        ...prev,
                        type: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="neutral">Neutral</MenuItem>
                    <MenuItem value="penner">Penner</MenuItem>
                    <MenuItem value="renner">Renner</MenuItem>
                    <MenuItem value="oos">Out of Stock</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Plenty-specific fields */}
              {(selectedArticle.source === "plenty" ||
                selectedArticle.source === "both") && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Article ID"
                      value={selectedArticle.articleid || ""}
                      onChange={(e) =>
                        setSelectedArticle((prev) => ({
                          ...prev,
                          articleid: e.target.value,
                        }))
                      }
                      disabled={selectedArticle.source === "both"}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Variant ID"
                      value={selectedArticle.variantid || ""}
                      onChange={(e) =>
                        setSelectedArticle((prev) => ({
                          ...prev,
                          variantid: e.target.value,
                        }))
                      }
                      disabled={selectedArticle.source === "both"}
                    />
                  </Grid>
                </>
              )}

              {/* Amazon-specific fields */}
              {(selectedArticle.source === "amazon" ||
                selectedArticle.source === "both") && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="ASIN"
                      value={selectedArticle.asin || ""}
                      onChange={(e) =>
                        setSelectedArticle((prev) => ({
                          ...prev,
                          asin: e.target.value,
                        }))
                      }
                      disabled={selectedArticle.source === "both"}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="FNSKU"
                      value={selectedArticle.fnsku || ""}
                      onChange={(e) =>
                        setSelectedArticle((prev) => ({
                          ...prev,
                          fnsku: e.target.value,
                        }))
                      }
                      disabled={selectedArticle.source === "both"}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="ROI %"
                      type="number"
                      value={selectedArticle.roi || ""}
                      onChange={(e) =>
                        setSelectedArticle((prev) => ({
                          ...prev,
                          roi: parseFloat(e.target.value) || null,
                        }))
                      }
                      InputProps={{
                        endAdornment: "%",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Days of Stock Left"
                      type="number"
                      value={selectedArticle.days_of_stock_left || ""}
                      onChange={(e) =>
                        setSelectedArticle((prev) => ({
                          ...prev,
                          days_of_stock_left: parseInt(e.target.value) || null,
                        }))
                      }
                    />
                  </Grid>
                </>
              )}

              {/* Common stock fields */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Netto Stock"
                  type="number"
                  value={selectedArticle.bi_netto_stock || 0}
                  onChange={(e) =>
                    setSelectedArticle((prev) => ({
                      ...prev,
                      bi_netto_stock: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Stock Value €"
                  type="number"
                  value={selectedArticle.stock_value || ""}
                  onChange={(e) =>
                    setSelectedArticle((prev) => ({
                      ...prev,
                      stock_value: parseFloat(e.target.value) || null,
                    }))
                  }
                />
              </Grid>

              {/* Plenty-specific metrics */}
              {(selectedArticle.source === "plenty" ||
                selectedArticle.source === "both") && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Sold Articles"
                      type="number"
                      value={selectedArticle.bi_selled_articles || 0}
                      onChange={(e) =>
                        setSelectedArticle((prev) => ({
                          ...prev,
                          bi_selled_articles: parseInt(e.target.value) || 0,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Gross Revenue €"
                      type="number"
                      value={selectedArticle.gross_revenue || 0}
                      onChange={(e) =>
                        setSelectedArticle((prev) => ({
                          ...prev,
                          gross_revenue: parseFloat(e.target.value) || 0,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Ratio"
                      type="number"
                      value={selectedArticle.ratio || 0}
                      onChange={(e) =>
                        setSelectedArticle((prev) => ({
                          ...prev,
                          ratio: parseFloat(e.target.value) || 0,
                        }))
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleEditArticle(selectedArticle)}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Article Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the article "
            {selectedArticle?.articlename || selectedArticle?.sku}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteArticle}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase History Dialog */}
      <Dialog
        open={purchaseHistoryDialog}
        onClose={async () => {
          setPurchaseHistoryDialog(false);
          setSelectedArticleForHistory(null);
          setPurchaseHistoryData(null);

          // Refresh the total stock value and articles without history
          await loadTotalStockValue();
          await loadSkusWithoutHistory();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Purchase History - {selectedArticleForHistory?.sku || ""}
          <Typography variant="body2" color="text.secondary">
            {selectedArticleForHistory?.articlename || ""}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {purchaseHistoryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : purchaseHistoryData ? (
            <Box>
              {/* Article Summary */}
              <Card sx={{ mb: 2, backgroundColor: "#f5f5f5" }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Current Stock
                      </Typography>
                      <Typography variant="h6">
                        {purchaseHistoryData.article?.bi_netto_stock || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Stock Value
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        €
                        {purchaseHistoryData.article?.stock_value?.toFixed(2) ||
                          "0.00"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Average Cost
                      </Typography>
                      <Typography variant="h6">
                        €
                        {purchaseHistoryData.average_cost?.toFixed(2) || "0.00"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Total Records
                      </Typography>
                      <Typography variant="h6">
                        {purchaseHistoryData.purchase_records?.length || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Add Record Button */}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddPurchaseRecord}
                >
                  Add Purchase Record
                </Button>
              </Box>

              {/* Purchase Records Table */}
              {purchaseHistoryData.purchase_records &&
              purchaseHistoryData.purchase_records.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Purchase Date</TableCell>
                        <TableCell align="right">Buy Price (netto)</TableCell>
                        <TableCell align="right">Units</TableCell>
                        <TableCell align="right">Total Cost</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseHistoryData.purchase_records.map(
                        (record, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              {new Date(
                                record.purchase_date,
                              ).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              €{record.buy_price?.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">{record.units}</TableCell>
                            <TableCell align="right">
                              €{(record.buy_price * record.units).toFixed(2)}
                            </TableCell>
                            <TableCell>{record.notes || "-"}</TableCell>
                            <TableCell>
                              {record.href === "Human" ? (
                                <Chip
                                  label="Human"
                                  size="small"
                                  color="primary"
                                />
                              ) : record.href ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  href={record.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ textTransform: "none" }}
                                >
                                  View Source
                                </Button>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditPurchaseRecord(record)}
                                title="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeletePurchaseRecord(record.id)
                                }
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No purchase history records found for this SKU. Click "Add
                  Purchase Record" to create one.
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="error">
              Failed to load purchase history data.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              setPurchaseHistoryDialog(false);
              setSelectedArticleForHistory(null);
              setPurchaseHistoryData(null);

              // Refresh the total stock value and articles without history
              await loadTotalStockValue();
              await loadSkusWithoutHistory();
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase History Edit Dialog */}
      <Dialog
        open={purchaseHistoryEditDialog}
        onClose={() => {
          setPurchaseHistoryEditDialog(false);
          setEditingPurchaseRecord(null);
          setPurchaseRecordForm({
            purchase_date: new Date().toISOString().split("T")[0],
            buy_price: "",
            units: "",
            notes: "",
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingPurchaseRecord
            ? "Edit Purchase Record"
            : "Add Purchase Record"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Purchase Date"
              type="date"
              value={purchaseRecordForm.purchase_date}
              onChange={(e) =>
                handlePurchaseRecordFormChange("purchase_date", e.target.value)
              }
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="Buy Price (netto) €"
              type="number"
              value={purchaseRecordForm.buy_price}
              onChange={(e) =>
                handlePurchaseRecordFormChange("buy_price", e.target.value)
              }
              fullWidth
              required
              inputProps={{ min: "0.01", step: "0.01" }}
            />
            <TextField
              label="Units"
              type="number"
              value={purchaseRecordForm.units}
              onChange={(e) =>
                handlePurchaseRecordFormChange("units", e.target.value)
              }
              fullWidth
              required
              inputProps={{ min: "1", step: "1" }}
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={purchaseRecordForm.notes}
              onChange={(e) =>
                handlePurchaseRecordFormChange("notes", e.target.value)
              }
              fullWidth
              placeholder="Optional notes about this purchase..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPurchaseHistoryEditDialog(false);
              setEditingPurchaseRecord(null);
              setPurchaseRecordForm({
                purchase_date: new Date().toISOString().split("T")[0],
                buy_price: "",
                units: "",
                notes: "",
              });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePurchaseRecord}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {editingPurchaseRecord ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stock Value History Dialog */}
      <Dialog
        open={stockValueHistoryDialog}
        onClose={() => setStockValueHistoryDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Stock Value History
          <Typography variant="body2" color="text.secondary">
            Historical stock value data over time
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Days of History"
              type="number"
              size="small"
              value={stockValueHistoryDays}
              onChange={(e) =>
                setStockValueHistoryDays(parseInt(e.target.value) || 30)
              }
              inputProps={{ min: 1, max: 365 }}
              sx={{ width: 150 }}
            />
            <Button
              variant="contained"
              onClick={() => loadStockValueHistory(stockValueHistoryDays)}
              disabled={stockValueHistoryLoading}
              startIcon={
                stockValueHistoryLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon />
                )
              }
            >
              {stockValueHistoryLoading ? "Loading..." : "Reload"}
            </Button>
          </Box>

          {stockValueHistoryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : stockValueHistoryData?.history ? (
            <Box>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Current Combined Value
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        €
                        {(
                          stockValueHistoryData.combined_stock_value ??
                          stockValueHistoryData.total_stock_value
                        )?.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Stock: €
                        {stockValueHistoryData.total_stock_value?.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Transit: €
                        {stockValueHistoryData.total_transit_stock_value?.toLocaleString() ||
                          "0"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Period
                      </Typography>
                      <Typography variant="h6">
                        {stockValueHistoryData.history.days} days
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(
                          stockValueHistoryData.history.start_date,
                        ).toLocaleDateString()}{" "}
                        -{" "}
                        {new Date(
                          stockValueHistoryData.history.end_date,
                        ).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Plenty Combined Value
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#90caf9" }}>
                        €
                        {(
                          stockValueHistoryData.by_source?.plenty
                            ?.combined_stock_value ??
                          stockValueHistoryData.by_source?.plenty?.stock_value
                        )?.toLocaleString() || "0"}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Stock: €
                        {stockValueHistoryData.by_source?.plenty?.stock_value?.toLocaleString() ||
                          "0"}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Transit: €
                        {stockValueHistoryData.by_source?.plenty?.transit_stock_value?.toLocaleString() ||
                          "0"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Amazon Combined Value
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#f48fb1" }}>
                        €
                        {(
                          stockValueHistoryData.by_source?.amazon
                            ?.combined_stock_value ??
                          stockValueHistoryData.by_source?.amazon?.stock_value
                        )?.toLocaleString() || "0"}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Stock: €
                        {stockValueHistoryData.by_source?.amazon?.stock_value?.toLocaleString() ||
                          "0"}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        Transit: €
                        {stockValueHistoryData.by_source?.amazon?.transit_stock_value?.toLocaleString() ||
                          "0"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Historical Data Table */}
              <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Date</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Combined Value</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Stock / Transit Value</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Plenty Comb. (Stock/Transit)</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Amazon Comb. (Stock/Transit)</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Total Articles</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Units (Stock/Transit)</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockValueHistoryData.history.data
                      .slice()
                      .reverse()
                      .map((dayData, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            {new Date(dayData.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: idx === 0 ? 600 : 400,
                                color:
                                  idx === 0 ? "success.main" : "text.primary",
                              }}
                            >
                              €
                              {(
                                dayData.combined_stock_value ??
                                dayData.total_stock_value
                              )?.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              €
                              {dayData.total_stock_value?.toLocaleString() ||
                                "0"}{" "}
                              / €
                              {dayData.total_transit_stock_value?.toLocaleString() ||
                                "0"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              €
                              {(
                                dayData.by_source?.plenty
                                  ?.combined_stock_value ??
                                dayData.by_source?.plenty?.stock_value
                              )?.toLocaleString() || "0"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              €
                              {dayData.by_source?.plenty?.stock_value?.toLocaleString() ||
                                "0"}{" "}
                              / €
                              {dayData.by_source?.plenty?.transit_stock_value?.toLocaleString() ||
                                "0"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              €
                              {(
                                dayData.by_source?.amazon
                                  ?.combined_stock_value ??
                                dayData.by_source?.amazon?.stock_value
                              )?.toLocaleString() || "0"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              €
                              {dayData.by_source?.amazon?.stock_value?.toLocaleString() ||
                                "0"}{" "}
                              / €
                              {dayData.by_source?.amazon?.transit_stock_value?.toLocaleString() ||
                                "0"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {dayData.articles_with_value}
                          </TableCell>
                          <TableCell align="right">
                            {dayData.total_stock_units} /{" "}
                            {dayData.total_transit_units || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Alert severity="info">
              No historical data available. Click "Reload" to fetch data.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockValueHistoryDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ArticlesManagementSubTab;
